import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import documentService from '../../services/documentService';
import { getDocumentTypeLabel } from '../../utils/documentTypes';
import { downloadDocumentPdf } from '../../utils/generateDocumentPdf';
import { buildSortedDocumentPrintLines } from '../../utils/sortPrintLineItems';
import { formatPrintDate } from '../../utils/dateUtils';

const PrintDocument = () => {
  const { id } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [id]);

  const loadDocument = async () => {
    try {
      const response = await documentService.getDocument(id);
      if (response.success) {
        setDocument(response.data);
      }
    } catch (error) {
      console.error('Error al cargar documento:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">Documento no encontrado</p>
        </div>
      </div>
    );
  }

  const issueDate = formatPrintDate(document.issue_date);

  const dueDate = document.due_date ? formatPrintDate(document.due_date) : '';

  const getDocumentTypeLabelPrint = () => getDocumentTypeLabel(document.document_type, { uppercase: true });

  // Condición de pago
  let paymentCondition = '';
  if (document.payment_condition_type === 'cash') {
    paymentCondition = 'Al contado';
  } else if (document.payment_condition_type === 'days') {
    paymentCondition = `Cada ${document.payment_condition_days} días`;
  } else if (document.payment_condition_type === 'date') {
    const paymentDateStr = document.payment_condition_date
      ? formatPrintDate(document.payment_condition_date)
      : '';
    paymentCondition = `Fecha específica: ${paymentDateStr}`;
  }

  // Fecha de pago
  const paymentDateFormatted = document.payment_date
    ? formatPrintDate(document.payment_date)
    : '';

  // Recalcular IGV y total según la tasa del documento para que el monto coincida con el % mostrado
  const baseNum = parseFloat(document.base_imponible || 0);
  const noGravadosNum = parseFloat(document.no_gravados || 0);
  const igvRate = document.igv_rate ?? 18;
  const igvNum = Math.round(baseNum * (igvRate / 100) * 100) / 100;
  const totalNum = Math.round((baseNum + igvNum + noGravadosNum) * 100) / 100;

  const baseImponible = baseNum.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const igvAmount = igvNum.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const noGravados = noGravadosNum.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const totalAmount = totalNum.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Convertir total a letras
  const numberToWords = (num) => {
    const entero = Math.floor(num);
    const decimal = Math.round((num - entero) * 100);
    return `${entero} CON ${decimal}/100 SOLES`;
  };
  const totalInWords = numberToWords(totalNum);

  const currencySymbol = document.currency === 'USD' ? '$' : 'S/';
  const formatMoney = (amount) =>
    `${currencySymbol} ${parseFloat(amount || 0).toLocaleString('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatActivoPath = (activo) => {
    const family = activo.family;
    if (!family) return '';
    const sub = family.subcategory;
    const cat = sub?.category;
    return [cat?.name, sub?.name, family.name].filter(Boolean).join(' › ');
  };

  const handleDownloadPdf = async () => {
    if (!document || pdfLoading) return;
    setPdfLoading(true);
    try {
      await downloadDocumentPdf(document);
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert(error?.message || 'No se pudo generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const activos = document.activos || [];
  const sortedPrintLines = buildSortedDocumentPrintLines(document);
  const hasLineItems = sortedPrintLines.length > 0;
  const activosOnly = activos.length > 0 && (document.items?.length || 0) === 0;
  const showDeliveryDate = document.delivery_date && !activosOnly;
  const showAdditionalDates =
    document.service_start_date || document.service_end_date || showDeliveryDate;
  const showFooterSection = document.notes || showAdditionalDates;

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 10mm 8mm;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            padding: 0;
            margin: 0;
            max-width: 100%;
            box-shadow: none;
            width: 100%;
            height: 100%;
          }
        }
        
        @media screen {
          body {
            background: #f3f4f6;
          }
          .print-container {
            max-width: 210mm;
            margin: 20px auto;
            background: white;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          .print-actions {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
            display: flex;
            gap: 10px;
          }
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          font-size: 11pt;
          color: #1f2937;
          line-height: 1.6;
          background: white;
        }
        
        .print-container {
          background: white;
          padding: 10mm 8mm;
          width: 210mm;
          min-height: 297mm;
        }
        
        @media print {
          .print-container {
            padding: 0;
            width: 100%;
            min-height: 100%;
          }
        }
        
        /* Header */
        .header {
          background: #0d9488;
          color: white;
          padding: 12px 15px;
          margin-bottom: 12px;
          border-radius: 0;
        }
        
        .header h1 {
          font-size: 18pt;
          font-weight: 700;
          margin: 0 0 4px 0;
          letter-spacing: -0.5px;
        }
        
        .header .code {
          font-family: 'Courier New', monospace;
          font-size: 11pt;
          font-weight: 600;
          letter-spacing: 1px;
        }
        
        /* Cards Grid */
        .cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }
        
        .card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 10px;
        }
        
        .card-title {
          font-size: 8.5pt;
          font-weight: 600;
          color: #0d9488;
          margin: 0 0 8px 0;
          padding-bottom: 4px;
          border-bottom: 1.5px solid #e5e7eb;
        }
        
        .field {
          margin-bottom: 6px;
        }
        
        .field-label {
          font-size: 6.5pt;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 2px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .field-value {
          font-size: 8pt;
          color: #1f2937;
          font-weight: 500;
          line-height: 1.3;
        }
        
        /* Items Card */
        .items-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 10px;
          margin-bottom: 12px;
        }
        
        .items-title {
          font-size: 8.5pt;
          font-weight: 600;
          color: #0d9488;
          margin: 0 0 8px 0;
          padding-bottom: 4px;
          border-bottom: 1.5px solid #e5e7eb;
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
          font-size: 7pt;
        }
        
        .items-table thead {
          background: #0d9488;
          color: white;
        }
        
        @media print {
          .items-table thead {
            background: #0d9488 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        .items-table th {
          padding: 6px 4px;
          text-align: left;
          font-weight: 600;
          font-size: 6.5pt;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border: none;
        }
        
        .items-table th:nth-child(1) {
          width: 40%;
        }
        .items-table th:nth-child(2) {
          width: 10%;
          text-align: center;
        }
        .items-table th:nth-child(3) {
          width: 12%;
          text-align: right;
        }
        .items-table th:nth-child(4) {
          width: 19%;
          text-align: right;
        }
        .items-table th:nth-child(5) {
          width: 19%;
          text-align: right;
        }
        
        .items-table tbody tr {
          border-bottom: 1px solid #e5e7eb;
        }
        
        .items-table tbody tr:last-child {
          border-bottom: none;
        }
        
        .items-table td {
          padding: 5px 4px;
          font-size: 7pt;
          vertical-align: top;
        }
        
        .items-table td:nth-child(2) {
          text-align: center;
        }
        .items-table td:nth-child(3),
        .items-table td:nth-child(4),
        .items-table td:nth-child(5) {
          text-align: right;
        }
        
        .product-description-wrapper {
          display: flex;
          flex-direction: column;
        }
        
        .product-name {
          font-size: 7pt;
          color: #1f2937;
          font-weight: 500;
          line-height: 1.3;
          word-wrap: break-word;
          margin-bottom: 2px;
        }
        
        .product-code {
          font-size: 6pt;
          font-weight: 400;
          color: #6b7280;
          font-family: 'Courier New', monospace;
          letter-spacing: 0.2px;
        }
        
        /* Totals */
        .totals-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
        }
        
        .totals-box {
          width: 240px;
          border: 1px solid #e5e7eb;
          padding: 10px;
          background: #f9fafb;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: 7.5pt;
        }
        
        .total-row:last-child {
          margin-bottom: 0;
          padding-top: 4px;
          border-top: 1.5px solid #0d9488;
          margin-top: 4px;
        }
        
        .total-label {
          color: #6b7280;
          font-weight: 500;
        }
        
        .total-value {
          color: #1f2937;
          font-weight: 600;
        }
        
        .total-row:last-child .total-label {
          font-size: 9pt;
          font-weight: 700;
          color: #0d9488;
        }
        
        .total-row:last-child .total-value {
          font-size: 11pt;
          font-weight: 700;
          color: #0d9488;
        }
        
        .total-in-words {
          margin-top: 6px;
          padding-top: 6px;
          border-top: 1px solid #e5e7eb;
          font-size: 6.5pt;
          color: #6b7280;
          font-style: italic;
        }
        
        /* Compact spacing for additional sections */
        .compact-section {
          margin-bottom: 10px;
        }
        
        .compact-field {
          margin-bottom: 4px;
        }
      `}</style>

      <div className="no-print print-actions">
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors disabled:opacity-60"
        >
          {pdfLoading ? 'Generando PDF…' : 'PDF'}
        </button>
        <button
          onClick={() => window.close()}
          className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium shadow-md transition-colors"
        >
          ✕ Cerrar
        </button>
      </div>

      <div className="print-container" ref={null}>
        {/* Header */}
        <div className="header">
          <h1>{getDocumentTypeLabelPrint()}</h1>
          <div className="code">{document.code}</div>
        </div>

        {/* Información Básica y Proveedor */}
        <div className="cards-grid">
          <div className="card">
            <div className="card-title">Información del Documento</div>
            <div className="field">
              <div className="field-label">Serie y Número</div>
              <div className="field-value">
                {document.series && document.number 
                  ? `${document.series}-${document.number}`
                  : document.series || document.number || 'N/A'}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Fecha de Emisión</div>
              <div className="field-value">{issueDate}</div>
            </div>
            {dueDate && (
              <div className="field">
                <div className="field-label">Fecha de Vencimiento</div>
                <div className="field-value">{dueDate}</div>
              </div>
            )}
            <div className="field">
              <div className="field-label">Moneda</div>
              <div className="field-value">{document.currency === 'PEN' ? 'SOLES (S/)' : 'DÓLARES (USD)'}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Proveedor</div>
            <div className="field">
              <div className="field-label">Razón Social</div>
              <div className="field-value">{document.supplier?.name || 'N/A'}</div>
            </div>
            {document.supplier?.document_number && (
              <div className="field">
                <div className="field-label">
                  {document.supplier.document_type === 'ruc' 
                    ? 'RUC'
                    : document.supplier.document_type === 'dni'
                    ? 'DNI'
                    : 'Código'}
                </div>
                <div className="field-value">{document.supplier.document_number}</div>
              </div>
            )}
            {document.supplier?.address && (
              <div className="field">
                <div className="field-label">Dirección</div>
                <div className="field-value">{document.supplier.address}</div>
              </div>
            )}
          </div>
        </div>

        {/* Referencias y Condición de Pago en una sola fila */}
        <div className="cards-grid">
          {/* Referencias (solo para notas de crédito/débito) */}
          {(document.document_type === 'nota_credito' || document.document_type === 'nota_debito') && 
           (document.reference_series || document.reference_number) && (
            <div className="card compact-section">
              <div className="card-title">Referencias</div>
              {document.reference_document_type && (
                <div className="field compact-field">
                  <div className="field-label">Tipo</div>
                  <div className="field-value">
                    {getDocumentTypeLabel(document.reference_document_type)}
                  </div>
                </div>
              )}
              {document.reference_document_date && (
                <div className="field compact-field">
                  <div className="field-label">Fecha</div>
                  <div className="field-value">
                    {formatPrintDate(document.reference_document_date)}
                  </div>
                </div>
              )}
              {(document.reference_series || document.reference_number) && (
                <div className="field compact-field">
                  <div className="field-label">Serie/Número</div>
                  <div className="field-value">
                    {document.reference_series && document.reference_number
                      ? `${document.reference_series}-${document.reference_number}`
                      : document.reference_series || document.reference_number || 'N/A'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Condición de Pago */}
          <div className="card compact-section">
            <div className="card-title">Condición de Pago</div>
            <div className="field compact-field">
              <div className="field-label">Tipo</div>
              <div className="field-value">{paymentCondition}</div>
            </div>
            {paymentDateFormatted && (
              <div className="field compact-field">
                <div className="field-label">Fecha de Pago</div>
                <div className="field-value">{paymentDateFormatted}</div>
              </div>
            )}
          </div>
        </div>

        {/* Referencias - Órdenes de Compra, Guías y Servicios Continuos */}
        {((document.purchase_orders && document.purchase_orders.length > 0) || 
          (document.entry_guides && document.entry_guides.length > 0) ||
          (document.continuous_services && document.continuous_services.length > 0)) && (
          <div className="items-card" style={{ marginBottom: '12px' }}>
            <div className="items-title">Referencias</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {/* Órdenes de Compra */}
              {document.purchase_orders?.map((po) => (
                <div key={po.id} style={{ 
                  flex: '1 1 280px', 
                  minWidth: '280px',
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '0', 
                  padding: '8px',
                  fontSize: '7pt'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: '600', color: '#6b7280' }}>Orden de Compra</span>
                    <span style={{ fontFamily: 'monospace', color: '#1f2937', fontWeight: '500' }}>{po.code}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', fontSize: '6.5pt' }}>
                    {po.supplier?.name && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Entidad: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>{po.supplier.name}</span>
                      </div>
                    )}
                    {po.issue_date && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Fecha: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>
                          {formatPrintDate(po.issue_date)}
                        </span>
                      </div>
                    )}
                    {po.total_amount && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Total: </span>
                        <span style={{ color: '#1f2937', fontWeight: '600' }}>
                          {po.currency || 'PEN'} {parseFloat(po.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Guías */}
              {document.entry_guides?.map((guide) => (
                <div key={guide.id} style={{ 
                  flex: '1 1 280px', 
                  minWidth: '280px',
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '0', 
                  padding: '8px',
                  fontSize: '7pt'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: '600', color: '#6b7280' }}>Guía</span>
                    <span style={{ fontFamily: 'monospace', color: '#1f2937', fontWeight: '500' }}>{guide.code}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', fontSize: '6.5pt' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Tipo: </span>
                      <span style={{ color: '#1f2937', fontWeight: '500', textTransform: 'capitalize' }}>
                        {guide.guide_type === 'entrada' ? 'Entrada' :
                         guide.guide_type === 'salida' ? 'Salida' :
                         guide.guide_type === 'inicial' ? 'Inicial' :
                         guide.guide_type === 'traspaso' ? 'Traspaso' :
                         guide.guide_type === 'recepcion' ? 'Recepción' :
                         guide.guide_type}
                      </span>
                    </div>
                    {guide.warehouse?.code && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Almacén: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>{guide.warehouse.code}</span>
                      </div>
                    )}
                    {guide.issue_date && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Fecha: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>
                          {formatPrintDate(guide.issue_date)}
                        </span>
                      </div>
                    )}
                    {guide.total_amount && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Total: </span>
                        <span style={{ color: '#1f2937', fontWeight: '600' }}>
                          {guide.currency || 'PEN'} {parseFloat(guide.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Servicios Continuos */}
              {document.continuous_services?.map((cs) => (
                <div key={cs.id} style={{ 
                  flex: '1 1 280px', 
                  minWidth: '280px',
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '0', 
                  padding: '8px',
                  fontSize: '7pt'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: '600', color: '#6b7280' }}>Servicio Continuo</span>
                    <span style={{ fontFamily: 'monospace', color: '#1f2937', fontWeight: '500' }}>{cs.code}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', fontSize: '6.5pt' }}>
                    {cs.supplier?.name && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Proveedor: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>{cs.supplier.name}</span>
                      </div>
                    )}
                    {cs.start_date && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Inicio: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>
                          {formatPrintDate(cs.start_date)}
                        </span>
                      </div>
                    )}
                    {cs.end_date && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Fin: </span>
                        <span style={{ color: '#1f2937', fontWeight: '500' }}>
                          {formatPrintDate(cs.end_date)}
                        </span>
                      </div>
                    )}
                    <div>
                      <span style={{ color: '#6b7280' }}>Estado: </span>
                      <span style={{ 
                        color: cs.status === 'closed' ? '#374151' : cs.status === 'open' ? '#059669' : '#1f2937',
                        fontWeight: '500'
                      }}>
                        {cs.status === 'closed' ? 'Cerrado' : cs.status === 'open' ? 'Abierto' : cs.status}
                      </span>
                    </div>
                    {cs.total_amount && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Total: </span>
                        <span style={{ color: '#1f2937', fontWeight: '600' }}>
                          PEN {parseFloat(cs.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="items-card">
          <div className="items-title">Items del Documento</div>
          <table className="items-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cant.</th>
                <th>P.Unit</th>
                <th>Subtotal</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedPrintLines.map((line, index) => {
                if (line.type === 'activo') {
                  const activo = line.data;
                  const qty = parseFloat(activo.acquisition_quantity) || 1;
                  const unitPrice = parseFloat(activo.acquisition_unit_price) || 0;
                  const lineSubtotal = Math.round(qty * unitPrice * 100) / 100;
                  const lineIgv = Math.round(lineSubtotal * (igvRate / 100) * 100) / 100;
                  const lineTotal = Math.round((lineSubtotal + lineIgv) * 100) / 100;
                  const classification = formatActivoPath(activo);

                  return (
                    <tr key={`activo-${activo.id ?? index}`}>
                      <td>
                        <div className="product-description-wrapper">
                          <span className="product-name">{activo.name || 'Activo'}</span>
                          <span className="product-code">
                            {activo.code}
                            {activo.brand || activo.model
                              ? ` · ${[activo.brand, activo.model].filter(Boolean).join(' · ')}`
                              : ''}
                          </span>
                          <span className="product-code" style={{ marginTop: '3px', color: '#4338ca' }}>
                            Activo fijo
                            {classification ? ` · ${classification}` : ''}
                            {activo.area?.name ? ` · Área: ${activo.area.name}` : ''}
                            {activo.serial_number ? ` · Serie: ${activo.serial_number}` : ''}
                          </span>
                        </div>
                      </td>
                      <td>{qty.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                      <td>{formatMoney(unitPrice)}</td>
                      <td>{formatMoney(lineSubtotal)}</td>
                      <td>{formatMoney(lineTotal)}</td>
                    </tr>
                  );
                }

                const item = line.data;
                return (
                  <tr key={`item-${item.id ?? index}`}>
                    <td>
                      <div className="product-description-wrapper">
                        <span className="product-name">
                          {item.item_type === 'product'
                            ? (item.product?.name || item.description || 'N/A')
                            : (item.service?.name || item.description || 'N/A')}
                        </span>
                        {(item.item_type === 'product' && item.product?.sku) ||
                         (item.item_type === 'service' && item.service?.sku) ? (
                          <span className="product-code">
                            SKU: {item.item_type === 'product' ? item.product.sku : item.service.sku}
                          </span>
                        ) : null}
                        {item.description && item.description !== (item.item_type === 'product' ? item.product?.name : item.service?.name) && (
                          <span className="product-code" style={{ marginTop: '3px' }}>
                            {item.description}
                          </span>
                        )}
                        {item.project && (
                          <span className="product-code" style={{ marginTop: '3px', color: '#0d9488' }}>
                            Proyecto: {item.project.code} - {item.project.name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{parseFloat(item.quantity).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                    <td>{formatMoney(item.unit_price)}</td>
                    <td>{formatMoney(item.subtotal)}</td>
                    <td>{formatMoney(item.total)}</td>
                  </tr>
                );
              })}
              {!hasLineItems && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280', padding: '12px' }}>
                    Sin ítems registrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className="totals-section">
            <div className="totals-box">
              <div className="total-row">
                <span className="total-label">Base Imponible:</span>
                <span className="total-value">{currencySymbol} {baseImponible}</span>
              </div>
              <div className="total-row">
                <span className="total-label">IGV ({document.igv_rate ?? 18}%):</span>
                <span className="total-value">{currencySymbol} {igvAmount}</span>
              </div>
              {parseFloat(document.no_gravados || 0) > 0 && (
                <div className="total-row">
                  <span className="total-label">No Gravados:</span>
                  <span className="total-value">{currencySymbol} {noGravados}</span>
                </div>
              )}
              <div className="total-row">
                <span className="total-label">Total:</span>
                <span className="total-value">{currencySymbol} {totalAmount}</span>
              </div>
              <div className="total-in-words">
                Son: {totalInWords}
              </div>
            </div>
          </div>
        </div>

        {/* Observaciones y Fechas Adicionales en una sola fila */}
        {showFooterSection && (
          <div className="cards-grid">
            {/* Observaciones */}
            {document.notes && (
              <div className="card compact-section">
                <div className="card-title">Observaciones</div>
                <div className="field-value" style={{ whiteSpace: 'pre-wrap', fontSize: '7pt', lineHeight: '1.3' }}>{document.notes}</div>
              </div>
            )}

            {/* Fechas de Servicio/Entrega */}
            {showAdditionalDates && (
              <div className="card compact-section">
                <div className="card-title">Fechas Adicionales</div>
                {document.service_start_date && (
                  <div className="field compact-field">
                    <div className="field-label">Inicio Servicio</div>
                    <div className="field-value">
                      {formatPrintDate(document.service_start_date)}
                    </div>
                  </div>
                )}
                {document.service_end_date && (
                  <div className="field compact-field">
                    <div className="field-label">Fin Servicio</div>
                    <div className="field-value">
                      {formatPrintDate(document.service_end_date)}
                    </div>
                  </div>
                )}
                {showDeliveryDate && (
                  <div className="field compact-field">
                    <div className="field-label">Fecha Entrega</div>
                    <div className="field-value">
                      {formatPrintDate(document.delivery_date)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PrintDocument;

